import { useEffect, useMemo, type CSSProperties, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { BeamFrame, usePointerSpotlight } from "@pontimed/ui/puls";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  Bell,
  Check,
  ChevronLeft,
  Download,
  Globe,
  Monitor,
  Plus,
  Share,
  Shield,
  Smartphone,
  WifiOff,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { useCountryRouteSync } from "@/lib/hooks/use-country-route-sync";
import { usePwaInstall } from "@/lib/hooks/use-pwa-install";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";
import { buildWebUrl } from "@/lib/web-url";

interface GetAppSearch {
  /** When true (e.g. arrived from apps/web header CTA), auto-fire the prompt
   *  on mount instead of waiting for the user to click "Install". */
  install?: boolean;
  /**
   * Country hint passed by apps/web cross-domain links — survives the jump
   * from `pontimed.app/{locale}/{country}/get-app` (legacy URL) to
   * `portal.pontimed.app/get-app?country=ro`. Synced into the atom by
   * `useCountryRouteSync()` below.
   */
  country?: string;
}

/**
 * Public install / "Get the app" page. Top-level route — accessible to both
 * logged-in and logged-out users (no _public or _authenticated wrapper).
 *
 * Strategy A end-to-end: apps/web (marketing on pontimed.app) cannot install
 * a PWA scoped to portal.pontimed.app (browsers enforce same-origin), so its
 * "Install App" CTA links here with `?install=1`. We then fire the
 * beforeinstallprompt directly when supported, or surface platform-specific
 * instructions otherwise.
 */
export const Route = createFileRoute("/get-app")({
  component: GetAppRoute,
  validateSearch: (search: Record<string, unknown>): GetAppSearch => ({
    // TanStack's default parseSearch is JSON-aware: `?install=1` arrives as
    // the number 1 (apps/web's portalLink sends exactly that).
    install: search.install === 1 || search.install === "1" || search.install === true,
    country: typeof search.country === "string" ? search.country.toUpperCase() : undefined,
  }),
});

type Platform = "android-chrome" | "ios-safari" | "desktop-chrome" | "other";

function detectPlatform(): Platform {
  if (typeof window === "undefined") return "other";
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !("MSStream" in window);
  if (isIOS) return "ios-safari";
  const isAndroid = /Android/.test(ua);
  const isChromium = /Chrome|Edg|Brave|Opera|Samsung/.test(ua);
  if (isAndroid && isChromium) return "android-chrome";
  if (isChromium) return "desktop-chrome";
  return "other";
}

function GetAppRoute() {
  const { install } = Route.useSearch();
  // Sync `?country=ro` (legacy cross-domain link from apps/web) into the
  // browsing-country atom so the rest of the SPA picks it up.
  useCountryRouteSync();
  const { canPrompt, isIos, isInstalled, promptInstall } = usePwaInstall();
  const platform = useMemo(detectPlatform, []);
  const router = useRouter();
  const { t, i18n } = useLingui();
  const { country: browsingCountry } = useBrowsingCountry();
  // The logo goes to the site in the portal's language + country, not the
  // bare origin (which picks both from www's own cookies / the browser).
  const webHome = buildWebUrl("", i18n.locale, browsingCountry ?? "ro");
  // Pointer-following border light on the card grids (`.puls-spot`).
  const spotlight = usePointerSpotlight();
  const handleBack = () => {
    // Prefer browser history; fall back to /find when the user landed
    // directly via deep link (no prior entry to pop).
    if (window.history.length > 1) router.history.back();
    else void router.navigate({ to: "/find" });
  };

  // Auto-fire the install prompt when arriving with ?install=1 — only valid
  // on Chromium-based browsers that have already emitted beforeinstallprompt.
  useEffect(() => {
    if (install && canPrompt && !isInstalled) {
      void promptInstall();
    }
  }, [install, canPrompt, isInstalled, promptInstall]);

  const handleInstall = async () => {
    if (canPrompt) await promptInstall();
  };

  const benefits: { icon: LucideIcon; tone: Tone; title: ReactNode; desc: ReactNode }[] = [
    {
      icon: Zap,
      tone: "warning",
      title: <Trans>Faster than the website</Trans>,
      desc: <Trans>App-like loading, no browser chrome eating up your screen.</Trans>,
    },
    {
      icon: Bell,
      tone: "primary",
      title: <Trans>Real-time notifications</Trans>,
      desc: <Trans>Get alerted when an earlier slot opens up or your appointment changes.</Trans>,
    },
    {
      icon: WifiOff,
      tone: "accent",
      title: <Trans>Works offline</Trans>,
      desc: <Trans>View saved appointments and search results without a connection.</Trans>,
    },
    {
      icon: Shield,
      tone: "success",
      title: <Trans>Private and secure</Trans>,
      desc: <Trans>Your data is encrypted in transit and at rest.</Trans>,
    },
    {
      icon: Smartphone,
      tone: "primary",
      title: <Trans>No app store needed</Trans>,
      desc: <Trans>Install in one tap. No 100 MB download, no account required upfront.</Trans>,
    },
    {
      icon: Globe,
      tone: "accent",
      title: <Trans>Always up to date</Trans>,
      desc: <Trans>Updates happen silently in the background. You always have the latest.</Trans>,
    },
  ];

  return (
    <>
      {/* Mini-header — back action on the left, centered logo. The /get-app
          page is reached as a side-trip from inside the portal (header /
          footer Install CTAs); centering the logo + a back arrow signals
          "you're inside a sub-flow, not on a top-level destination". */}
      <header className="border-border/50 bg-background/80 sticky top-0 z-50 border-b backdrop-blur-xl">
        <nav className="relative mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={handleBack}
            aria-label={t`Back`}
            className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-9 min-h-[44px] items-center gap-1 rounded-lg pr-3 pl-2 text-sm font-medium transition-colors"
          >
            <ChevronLeft className="size-5" aria-hidden />
            <Trans>Back</Trans>
          </button>
          <a
            href={webHome}
            className="absolute left-1/2 flex -translate-x-1/2 items-center"
            aria-label="PontiMed"
          >
            <img
              src="/icons/logo-black.png"
              alt="PontiMed"
              width={2100}
              height={450}
              className="h-8 w-auto sm:h-10 dark:hidden"
            />
            <img
              src="/icons/logo-white.png"
              alt="PontiMed"
              width={2100}
              height={450}
              className="hidden h-8 w-auto sm:h-10 dark:block"
            />
          </a>
        </nav>
      </header>

      <main className="min-h-dvh">
        {/* ── Hero — copy left, "home screen" visual right (lg+) ── */}
        <section className="relative isolate">
          <HeroBackdrop />
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pt-10 pb-16 sm:px-6 sm:pt-16 sm:pb-20 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-14 lg:px-8 lg:pt-20 lg:pb-24">
            <div className="flex min-w-0 flex-col items-start gap-6">
              <img
                src="/icons/icon-192.png"
                alt="PontiMed"
                width={64}
                height={64}
                className="puls-reveal ring-border/60 size-16 rounded-2xl shadow-lg ring-1 lg:hidden"
              />

              {!isInstalled && (
                <p
                  className="puls-reveal text-primary m-0 inline-flex items-center gap-2 font-mono text-xs font-medium tracking-[0.12em] uppercase"
                  style={stagger(1)}
                >
                  <span className="puls-live-dot" aria-hidden="true" />
                  <Trans>Free · No app store · No download</Trans>
                </p>
              )}

              <h1 className={H1}>
                <span className="puls-reveal" style={stagger(2)}>
                  <Trans>Install PontiMed</Trans>
                </span>
              </h1>
              <p
                className="puls-reveal text-muted-foreground m-0 max-w-2xl text-base leading-relaxed sm:text-lg"
                style={stagger(3)}
              >
                <Trans>Add PontiMed to your home screen — fast, offline-ready, no app store.</Trans>
              </p>

              <div
                className="puls-reveal flex w-full flex-col gap-3 sm:w-auto sm:flex-row"
                style={stagger(4)}
              >
                {isInstalled ? (
                  <div className="border-success-border bg-success-soft text-success-strong inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border px-6 text-sm font-semibold">
                    <Check className="size-4" aria-hidden />
                    <Trans>App already installed</Trans>
                  </div>
                ) : (
                  <BeamFrame className="rounded-lg">
                    {canPrompt ? (
                      <button
                        type="button"
                        onClick={handleInstall}
                        className={`${BTN_PRIMARY} w-full`}
                      >
                        <Download className="size-5" aria-hidden />
                        <Trans>Install now</Trans>
                      </button>
                    ) : (
                      <a href="#how-to-install" className={`${BTN_PRIMARY} w-full`}>
                        <Download className="size-5" aria-hidden />
                        <Trans>How to install</Trans>
                      </a>
                    )}
                  </BeamFrame>
                )}
              </div>
            </div>

            <HomeScreenVisual />
          </div>
        </section>

        {/* ── Benefits ── */}
        <section className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className={H2}>
              <Trans>Why install</Trans>
            </h2>

            <div
              className="puls-spot-group mt-10 grid gap-4 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3"
              onPointerMove={spotlight}
            >
              {benefits.map((b, i) => (
                <div key={i} className={`${CARD} flex flex-col gap-4`} style={toneStyle(b.tone)}>
                  <IconTile icon={b.icon} tone={b.tone} />
                  <h3 className={H3}>{b.title}</h3>
                  <p className="text-muted-foreground m-0 text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How to install ── */}
        <section id="how-to-install" className="bg-muted/35 relative scroll-mt-20 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <header className="grid max-w-3xl gap-3">
              <h2 className={H2}>
                <Trans>How to install</Trans>
              </h2>
              <p className={`${LEAD} max-w-[62ch]`}>
                {platform === "ios-safari" && (
                  <Trans>Looks like you&apos;re on iPhone — follow the iOS card below.</Trans>
                )}
                {(platform === "android-chrome" || platform === "desktop-chrome") && (
                  <Trans>
                    Use the Chrome / Android card — your browser supports one-tap install.
                  </Trans>
                )}
                {platform === "other" && (
                  <Trans>Pick the card matching your device for step-by-step instructions.</Trans>
                )}
              </p>
            </header>

            <div
              className="puls-spot-group mt-10 grid gap-4 sm:mt-12 md:grid-cols-2 lg:grid-cols-3"
              onPointerMove={spotlight}
            >
              {/* Chrome / Edge / Android */}
              <div
                className={`${CARD} flex flex-col gap-4 ${
                  platform === "android-chrome" || platform === "desktop-chrome"
                    ? "ring-primary/40 ring-2"
                    : ""
                }`}
                style={toneStyle("primary")}
              >
                <div className="flex items-center gap-3">
                  <IconTile icon={Monitor} tone="primary" />
                  <h3 className={H3}>
                    <Trans>Chrome, Edge, Android</Trans>
                  </h3>
                </div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  <Trans>One-tap install via the browser&apos;s native prompt.</Trans>
                </p>
                <ol className="m-0 list-none space-y-3 p-0">
                  <li className={STEP}>
                    <span className={STEP_NO}>01</span>
                    <span className="text-muted-foreground">
                      <Trans>Tap the install icon in the address bar (or three-dot menu).</Trans>
                    </span>
                  </li>
                  <li className={STEP}>
                    <span className={STEP_NO}>02</span>
                    <span className="text-muted-foreground">
                      <Trans>Choose &ldquo;Install PontiMed&rdquo;.</Trans>
                    </span>
                  </li>
                  <li className={STEP}>
                    <span className={STEP_NO}>03</span>
                    <span className="text-muted-foreground">
                      <Trans>Open it from your home screen — runs like a native app.</Trans>
                    </span>
                  </li>
                </ol>
                {canPrompt && (
                  <button
                    type="button"
                    onClick={handleInstall}
                    className={`${BTN_PRIMARY} mt-auto w-full`}
                  >
                    <Download className="size-4" aria-hidden />
                    <Trans>Install now</Trans>
                  </button>
                )}
              </div>

              {/* iOS Safari */}
              <div
                className={`${CARD} flex flex-col gap-4 ${
                  platform === "ios-safari" || isIos ? "ring-primary/40 ring-2" : ""
                }`}
                style={toneStyle("accent")}
              >
                <div className="flex items-center gap-3">
                  <IconTile icon={Smartphone} tone="accent" />
                  <h3 className={H3}>
                    <Trans>iPhone, iPad — Safari</Trans>
                  </h3>
                </div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  <Trans>Apple requires Safari for installing web apps.</Trans>
                </p>
                <ol className="m-0 list-none space-y-3 p-0">
                  <li className={STEP}>
                    <span className={STEP_NO}>01</span>
                    <span className="text-muted-foreground">
                      <Share className="text-info mr-1.5 inline size-4 align-[-3px]" aria-hidden />
                      <Trans>Tap the Share button at the bottom of the screen.</Trans>
                    </span>
                  </li>
                  <li className={STEP}>
                    <span className={STEP_NO}>02</span>
                    <span className="text-muted-foreground">
                      <Plus
                        className="text-foreground mr-1.5 inline size-4 align-[-3px]"
                        aria-hidden
                      />
                      <Trans>Choose &ldquo;Add to Home Screen&rdquo;.</Trans>
                    </span>
                  </li>
                  <li className={STEP}>
                    <span className={STEP_NO}>03</span>
                    <span className="text-muted-foreground">
                      <Trans>Tap &ldquo;Add&rdquo; in the top-right corner.</Trans>
                    </span>
                  </li>
                </ol>
              </div>

              {/* Other browsers */}
              <div className={`${CARD} flex flex-col gap-4`} style={toneStyle("warning")}>
                <div className="flex items-center gap-3">
                  <IconTile icon={Globe} tone="warning" />
                  <h3 className={H3}>
                    <Trans>Other browsers</Trans>
                  </h3>
                </div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  <Trans>Quick reference for browsers without a native install prompt.</Trans>
                </p>
                <ul className="m-0 list-none space-y-3 p-0 text-sm">
                  <li className={BULLET}>
                    <span className={BULLET_DOT} aria-hidden="true" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground font-semibold">Firefox Android:</strong>{" "}
                      <Trans>Three-dot menu → &ldquo;Install&rdquo;.</Trans>
                    </span>
                  </li>
                  <li className={BULLET}>
                    <span className={BULLET_DOT} aria-hidden="true" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground font-semibold">Safari macOS:</strong>{" "}
                      <Trans>File menu → &ldquo;Add to Dock&rdquo;.</Trans>
                    </span>
                  </li>
                  <li className={BULLET}>
                    <span className={BULLET_DOT} aria-hidden="true" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground font-semibold">
                        Brave / Opera / Vivaldi:
                      </strong>{" "}
                      <Trans>Same as Chrome — install icon in the address bar.</Trans>
                    </span>
                  </li>
                  <li className={BULLET}>
                    <span className={BULLET_DOT} aria-hidden="true" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground font-semibold">Firefox Desktop:</strong>{" "}
                      <Trans>No PWA install. Use Chrome / Edge for the full experience.</Trans>
                    </span>
                  </li>
                  <li className={BULLET}>
                    <span className={BULLET_DOT} aria-hidden="true" />
                    <span className="text-muted-foreground">
                      <strong className="text-foreground font-semibold">Samsung Internet:</strong>{" "}
                      <Trans>Menu → &ldquo;Add page to&rdquo; → &ldquo;Home screen&rdquo;.</Trans>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── What is PWA ── explanation left, native-apps note right (lg+) */}
        <section className="relative py-16 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8">
            <div className="grid content-start gap-3">
              <h2 className={H2}>
                <Trans>What is a PWA?</Trans>
              </h2>
              <p className={`${LEAD} max-w-[62ch]`}>
                <Trans>
                  A Progressive Web App combines the best of a website (no download, no store,
                  instant updates) with the best of a native app (offline, notifications, home
                  screen icon). Modern, lightweight, secure.
                </Trans>
              </p>

              <ul className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
                {[
                  <Trans key="t1">No download</Trans>,
                  <Trans key="t2">Auto-updates</Trans>,
                  <Trans key="t3">Lightweight</Trans>,
                  <Trans key="t4">Secure (HTTPS)</Trans>,
                  <Trans key="t5">Cross-platform</Trans>,
                ].map((tag, i) => (
                  <li
                    key={i}
                    className="border-border bg-card text-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium"
                  >
                    <Check className="text-primary size-3.5" aria-hidden />
                    {tag}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`${CARD} flex items-start gap-4`} style={toneStyle("accent")}>
              <IconTile icon={Smartphone} tone="accent" />
              <div className="grid min-w-0 gap-2">
                <h3 className={H3}>
                  <Trans>Native iOS / Android apps coming soon</Trans>
                </h3>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  <Trans>
                    We&apos;re building native apps with Capacitor for App Store and Google Play.
                    Until then, the PWA gives you the same offline-first experience.
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

/* ─────────────── Puls presentation ───────────────
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + glow behind the hero, mono
   eyebrow, display headings, tone-washed cards with the pointer light
   (`.puls-spot` + `usePointerSpotlight`), icon tiles, one BeamFrame CTA. */

type Tone = "primary" | "accent" | "success" | "warning";

/** `--puls-i` stagger for `.puls-reveal` (80 ms per step, see puls.css). */
const stagger = (i: number) => ({ "--puls-i": i }) as CSSProperties;

/** Soft tone wash at the top of a card + the spotlight tone. */
const toneStyle = (tone: Tone) =>
  ({
    "--puls-tone": `var(--${tone})`,
    backgroundImage: `linear-gradient(180deg, color-mix(in oklab, var(--${tone}-soft) 75%, var(--card)) 0%, var(--card) 55%)`,
  }) as CSSProperties;

const TONE_TEXT: Record<Tone, string> = {
  primary: "text-primary",
  accent: "text-accent",
  success: "text-success",
  warning: "text-warning",
};

const H1 =
  "font-display text-foreground m-0 text-[clamp(2.25rem,5vw,4rem)] leading-[1.04] font-semibold tracking-[-0.035em] text-balance";
const H2 =
  "font-display text-foreground m-0 text-[clamp(1.75rem,3.6vw,2.75rem)] leading-[1.08] font-semibold tracking-[-0.025em] text-balance";
const H3 = "font-display text-foreground m-0 text-lg leading-snug font-semibold tracking-[-0.01em]";
const LEAD = "text-muted-foreground m-0 text-base leading-relaxed sm:text-[17px]";
const CARD = "puls-spot border-border bg-card rounded-2xl border p-6";
const BTN_PRIMARY =
  "bg-primary text-primary-foreground hover:bg-primary-hover focus-visible:ring-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-6 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:text-base";
const STEP = "flex items-start gap-3 text-sm leading-5";
const STEP_NO = "text-primary w-5 shrink-0 font-mono text-xs leading-5 font-semibold tabular-nums";
const BULLET = "flex items-start gap-2.5";
const BULLET_DOT = "bg-primary mt-2 size-1.5 shrink-0 rounded-full";

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 75% 70% at 35% 25%, black 20%, transparent 75%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};

function HeroBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[8%] -right-[10%] h-[90%] w-[70%] blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

function IconTile({ icon: Icon, tone }: { icon: LucideIcon; tone: Tone }) {
  return (
    <span
      aria-hidden="true"
      className={`border-border bg-card grid size-11 shrink-0 place-items-center rounded-lg border ${TONE_TEXT[tone]}`}
    >
      <Icon className="size-5" />
    </span>
  );
}

/** Soft placeholder tiles of a phone home screen; `null` = the PontiMed icon. */
const HOME_TILES: (string | null)[] = [
  "bg-muted",
  "bg-accent-soft",
  "bg-muted/70",
  "bg-success-soft",
  "bg-warning-soft",
  null,
  "bg-muted",
  "bg-primary-soft",
  "bg-muted/70",
  "bg-muted",
  "bg-accent-soft",
  "bg-muted/70",
];

/** Decorative "added to the home screen" visual next to the hero copy (lg+). */
function HomeScreenVisual() {
  return (
    <div
      aria-hidden="true"
      className="puls-reveal hidden justify-self-center lg:block"
      style={stagger(3)}
    >
      <div className="border-border bg-card rounded-2xl border p-6 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)]">
        <div className="grid grid-cols-4 gap-5">
          {HOME_TILES.map((tile, i) =>
            tile === null ? (
              <span key={i} className="puls-hot-ring relative block size-16 rounded-xl">
                <img
                  src="/icons/icon-192.png"
                  alt=""
                  width={64}
                  height={64}
                  className="ring-border/60 size-16 rounded-xl shadow-lg ring-1"
                />
                <span className="bg-primary text-primary-foreground ring-card absolute -top-1.5 -right-1.5 grid size-6 place-items-center rounded-full ring-2">
                  <Plus className="size-3.5" strokeWidth={3} />
                </span>
              </span>
            ) : (
              <span key={i} className={`block size-16 rounded-xl ${tile}`} />
            ),
          )}
        </div>
        <div className="border-border mt-6 flex justify-center gap-5 border-t border-dashed pt-6">
          <span className="bg-muted block size-12 rounded-xl" />
          <span className="bg-muted/70 block size-12 rounded-xl" />
          <span className="bg-muted block size-12 rounded-xl" />
        </div>
      </div>
    </div>
  );
}
